import { observes } from "@ember-decorators/object"
import Component from "@ember/component"
import { action, computed, get, set } from "@ember/object"
import { later, next } from "@ember/runloop"
import { inject as service } from "@ember/service"
import type { Course, Unit } from "@shared/models/course"
import belongsTo from "cc-frontend/helpers/belongs-to"
import COURSE_SET_RECENTLY_IMPORTED_COURSES from "cc-frontend/lib/actions/course/COURSE_SET_RECENTLY_IMPORTED_COURSES"
import importTransferrableArrayAction from "cc-frontend/lib/actions/course/calendar/IMPORT_TRANSFERRABLE_ARRAY"
import { extractSelectedDates } from "cc-frontend/lib/transferrable-date-utils"
import { consolidateCourseDatesOff } from "cc-frontend/lib/unit-utils"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import { isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import _ from "lodash"
import { isNil } from "lodash-es"
import findDocument from "../../../helpers/find-document"

interface ModelArg {
  course: Course
  dateString: string
}

export default class ImportLessonsComponent extends Component {
  // Args
  model!: ModelArg
  closeModal!: Function

  // Properties
  courseToImportIntoId: string | null = null
  selectedCourseId: string | null = null
  selectedPlanbookId: string | null = null
  isImporting: boolean = false
  hasSelectedCourse: boolean = true
  courseSelectDialogIsOpen: boolean = false
  selectedUnitIds: Array<string> = []
  selectedDateStrings: Array<string> = []
  focusedItemId: string = ""

  // Services
  @service modalManager!: ModalManager
  @service dispatcher!: Dispatcher
  @service("ember-store") store!: EmberStore
  @service rpc!: Rpc

  constructor(...args) {
    // @ts-ignore
    super(...args)

    next(this, () => {
      // Must call the computed properties so we have the models fetched when we're ready to import
      // @ts-ignore
      get(this, "courseToImportInto.content")
      // @ts-ignore
      get(this, "planbookToImportInto.content")
      // @ts-ignore
      get(this, "rotationCalendarToImportInto.content")

      $("body").addClass("modal-open")
      if (this.model.course.attributes.recentlyImportedCourses.length === 0) {
        set(this, "courseSelectDialogIsOpen", true)
      } else {
        let lastUsed = _.first(this.model.course.attributes.recentlyImportedCourses)
        if (lastUsed) {
          set(this, "selectedCourseId", lastUsed.courseId)
          set(this, "selectedPlanbookId", lastUsed.planbookId)
          set(this, "focusedItemId", lastUsed.focusedItemId)
        }
      }
    })
  }

  setRecentlyImportedCourses(planbookId, courseId, focusedItemId) {
    let recentlyImportedCourses = this.courseToImportInto.content.attributes.recentlyImportedCourses
    let newArray = _.reject(recentlyImportedCourses, (el) => el.courseId == courseId)
    newArray.unshift({
      courseId: courseId,
      planbookId: planbookId,
      focusedItemId: focusedItemId,
      lastAccessed: new Date().toISOString(),
    })
    get(this, "dispatcher").dispatchAction(COURSE_SET_RECENTLY_IMPORTED_COURSES, {
      courseId: this.courseToImportInto.content.id,
      recentlyImportedCourses: newArray,
    })
  }

  /**
  * setFocusedItemId
  * This is a bit complex -- it finds the date to focus on in a prior year (most likely)
  * by looking at the current month/date and then  scanning  through all the rotationCalendar dates until
  * it finds it's one or the closest next one
  *
  * The reason we use an observer is we don't know what the rotationCalendar is when the course is selected

  * @return null
  */

  @observes("selectedCourseId", "rotationCalendar.isFulfilled")
  setFocusedItemId() {
    if (this.focusedItemId !== null && this.focusedItemId !== undefined) return
    if (this.selectedRotationCalendar.isFulfilled !== true) return

    let possibleYears = this.selectedPlanbook.content.attributes.years
    let currentMonthDate = dateFns.format(new Date(), "MM-DD")
    let possibleDates = _.flatMap(possibleYears, (year) => {
      return `${year}-${currentMonthDate}`
    })
    let focusedItemId = _.chain(this.selectedRotationCalendar.content.attributes.dates)
      .find((date, index, arr) => {
        let nextDateString = arr[index + 1] ? arr[index + 1].attributes.dateString : null
        return _.chain(possibleDates)
          .map((dateString) => {
            return (
              dateString === date.attributes.dateString ||
              (dateString >= date.attributes.dateString && dateString <= nextDateString)
            )
          })
          .includes(true)
          .value()
      })
      // @ts-ignore - apparently get can't be used in chains?
      // also, what object are we working with here?
      .get("attributes.dateString")
      .value()

    if (focusedItemId === undefined) {
      focusedItemId = `${_.first(possibleYears)}-09-04`
    }

    set(this, "focusedItemId", focusedItemId)
  }

  // Relationships
  @findDocument("selectedCourseId", "course") selectedCourse

  @findDocument("selectedPlanbookId", "planbook") selectedPlanbook

  @belongsTo("selectedPlanbook", "rotationCalendar") selectedRotationCalendar

  @findDocument("model.course.id", "course") courseToImportInto

  @belongsTo("courseToImportInto", "planbook") planbookToImportInto

  @belongsTo("planbookToImportInto", "rotationCalendar") rotationCalendarToImportInto

  // CPs/getters

  @computed("selectedDateStrings.length", "selectedUnitIds.length")
  get hasSelectedUnitsOrDates() {
    return get(this, "selectedDateStrings").length > 0 || get(this, "selectedUnitIds").length > 0
  }

  // Actions

  @action
  onDismissCourseSelectDialog() {
    this.setProperties({
      courseSelectDialogIsOpen: false,
    })
  }

  @action
  onToggleCourseSelectDialog() {
    this.setProperties({
      courseSelectDialogIsOpen: !get(this, "courseSelectDialogIsOpen"),
    })
  }

  @action
  onSelectCourse(planbookId, courseId, focusedItemId) {
    set(this, "courseSelectDialogIsOpen", false)
    later(() => {
      this.setProperties({
        focusedItemId: focusedItemId,
        hasSelectedCourse: true,
        selectedPlanbookId: planbookId,
        selectedCourseId: courseId,
      })
      this.setRecentlyImportedCourses(planbookId, courseId, focusedItemId)
    }, 300)
  }

  @action
  onLessonSelect(_lessonId, dateString) {
    let selectedDates = this.selectedDateStrings
    if (_.includes(selectedDates, dateString)) {
      selectedDates.removeObject(dateString)
    } else {
      selectedDates.addObject(dateString)
    }
  }

  @action
  onUnitSelect(unit: Unit) {
    if (isNil(unit)) return

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      this.selectedCourse.content,
      this.selectedPlanbook.content,
      this.selectedRotationCalendar.content
    )
    let dateStrings = _.chain((this.selectedCourse.content as Course).attributes.calendar.dates)
      .filter(isCourseDateCustom)
      .filter((date) => {
        return (
          date.attributes.dateString >= unit.startDate && date.attributes.dateString <= unit.endDate
        )
      })
      .filter((date) => !consolidatedCourseDatesOff.includes(date.attributes.dateString))
      .map((date) => date.attributes.dateString)
      .value()
    let unitIds: Array<string> = []
    unitIds.push(unit.id)
    unitIds = unitIds.concat(unit.descendantIds)

    let selectedDateStrings = this.selectedDateStrings
    let shouldRemove = _.includes(this.selectedUnitIds, unit.id)
    _.forEach(dateStrings, (dateString) => {
      if (shouldRemove) {
        selectedDateStrings.removeObject(dateString)
      } else {
        selectedDateStrings.addObject(dateString)
      }
    })

    let selectedUnitIds = get(this, "selectedUnitIds")
    _.forEach(unitIds, (id) => {
      shouldRemove ? selectedUnitIds.removeObject(id) : selectedUnitIds.addObject(id)
    })
  }

  @action
  importCardStacks() {
    set(this, "isImporting", true)
    let selectedDateStrings = get(this, "selectedDateStrings")
    let selectedUnitIds = get(this, "selectedUnitIds")
    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      this.selectedCourse.content,
      this.selectedPlanbook.content,
      this.selectedRotationCalendar.content
    )
    let transferrableArray = extractSelectedDates(
      this.selectedCourse.content.attributes.calendar.dates,
      selectedDateStrings,
      selectedUnitIds,
      consolidatedCourseDatesOff
    )

    get(this, "rpc")
      .execute("CARD_STACK_COPY_TRANSFERRABLE_ARRAY", {
        newCourseId: this.courseToImportInto.content.id,
        transferrableArray: transferrableArray,
        originalPlanbookId: this.selectedPlanbook.content.id,
        originalCourseId: this.selectedCourseId,
        originalOwnerIds: [this.selectedPlanbook.content.relationships.owner.data.id],
      })
      .then((ret) => {
        let newArray = ret.data.newTransferrableArray

        get(this, "dispatcher").dispatchPreparedAction(
          importTransferrableArrayAction({
            transferrableArray: newArray,
            course: this.courseToImportInto.content,
            planbook: this.planbookToImportInto.content,
            rotationCalendar: this.rotationCalendarToImportInto.content,
            newStartDate: this.model.dateString,
            originalCourseId: this.selectedCourseId,
            originalPlanbookId: this.selectedPlanbookId,
          })
        )
        this.closeModal()
      })
  }

  @action
  onNewFocusedItemId(id) {
    this.set("focusedItemId", id)
  }
}
